/**
 * @fileoverview Type-safe property accessors for the resource system
 * 
 * This module provides functions for safely accessing properties of resources
 * that may exist on some resource types but not others. Each function uses
 * the appropriate type guards to ensure type safety and provides a consistent
 * interface for accessing resource properties.
 */

import { siteConfig } from '~/lib/config/site'
import type { ResourceInstance, ResourceImage, MetadataConfig } from '~/lib/resources/types/shared/base'
import type { RelatedTerm } from '~/lib/resources/types/glossary/terms'
import type { FAQItem } from '~/components/shared/FAQ'
import { 
  isGlossaryTerm,
  isGuide,
  isTemplate,
  isTool,
  isQuiz,
  isResourceType,
  isCaseStudy,
  isEbook,
  isCalculator,
  isGenerator
} from '~/lib/resources/types/shared/type-guards'
import type { ResourceType, ToolType, ToolCategory, OperationType } from '~/lib/resources/types/shared/enums'
import { isSpecsGuide } from '~/lib/resources/types/guides/specs'
import type { TemplateCategoryType, TemplateType } from '~/lib/resources/types/templates'

/**
 * Gets the title for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string} The resource title
 */
export function getResourceTitle(resource: ResourceInstance): string {
  return resource.title
}

/**
 * Gets the description for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string} The resource description
 */
export function getResourceDescription(resource: ResourceInstance): string {
  if (isGlossaryTerm(resource)) {
    return resource.description ?? resource.shortDescription ?? resource.definition ?? ''
  }
  return resource.description ?? ''
}

/**
 * Gets the category for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string} The category for the resource or empty string if not available
 */
export function getResourceCategory(resource: ResourceInstance): string {
  if (isGlossaryTerm(resource)) {
    return resource.category
  }
  
  if (isTool(resource)) {
    return resource.toolCategory ?? ''
  }

  if (isTemplate(resource)) {
    return resource.category ?? ''
  }

  // Guides and ebooks carry a free-form `category` string used in fallback
  // `<title>` templates (`… - {category} Guide`). Returning '' here made
  // `formatResourceTitle` emit the double-space artifact `… -  Guide` whenever
  // an explicit metadata.title was rejected (#648 r3634866039).
  if (isGuide(resource) || isEbook(resource)) {
    return resource.category ?? ''
  }

  return ''
}

/**
 * Gets the tool category for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {ToolCategory | undefined} The tool category if it's a tool, undefined otherwise
 * 
 * @see {@link ToolCategory}
 * @note ToolCategory is a string literal type with the following values (as of 2025-03-23):
 *   "analytics" | "optimization" | "platform" | "strategy" | "creative" | 
 *   "audience" | "performance" | "testing" | "automation" | "data-analysis" | 
 *   "campaign-management" | "audience-targeting" | "budget-planning"
 */
export function getResourceToolCategory(resource: ResourceInstance): ToolCategory | undefined {
  if (isTool(resource)) {
    return resource.toolCategory
  }
  return undefined
}

/**
 * Gets the resource type identifier
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {ResourceType} The resource type
 * 
 * @see {@link ResourceType}
 * @note ResourceType is a string literal type with the following values (as of 2025-03-23):
 *   "guide" | "tool" | "template" | "case-study" | "ebook" | "glossary-term"
 */
export function getResourceType(resource: ResourceInstance): ResourceType {
  return resource.resourceType
}

/**
 * Gets the tool type for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {ToolType | undefined} The tool type for the resource or undefined if not a tool
 * 
 * @see {@link ToolType}
 * @note ToolType is a string literal type with the following values (as of 2025-03-23):
 *   "calculator" | "analyzer" | "generator" | "quiz"
 */
export function getResourceToolType(resource: ResourceInstance): ToolType | undefined {
  if (isTool(resource)) {
    return resource.toolType
  }
  return undefined
}

/**
 * Gets the tool type for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} The tool type for the resource or undefined if not a tool
 */
export function getToolType(resource: ResourceInstance): string | undefined {
  if (isTool(resource)) {
    return resource.toolType
  }
  return undefined
}

/**
 * Gets the slug for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string} The resource slug
 */
export function getResourceSlug(resource: ResourceInstance): string {
  return resource.slug
}

/**
 * Gets the hero image for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {ResourceImage | undefined} The hero image or undefined if not available
 * 
 * @see {@link ResourceImage}
 * @note ResourceImage is an interface with the following properties (as of 2025-03-23):
 *   - url: string - Image URL
 *   - width?: number - Image width in pixels
 *   - height?: number - Image height in pixels
 *   - alt?: string - Alt text for accessibility
 *   - caption?: string - Optional caption text
 */
export function getResourceHeroImage(resource: ResourceInstance): ResourceImage | undefined {
  if (resource.images?.hero) {
    return resource.images.hero
  }
  return undefined
}

/**
 * Gets the thumbnail image for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {ResourceImage | undefined} The thumbnail image or undefined if not available
 * 
 * @see {@link ResourceImage}
 * @note ResourceImage is an interface with the following properties (as of 2025-03-23):
 *   - url: string - Image URL
 *   - width?: number - Image width in pixels
 *   - height?: number - Image height in pixels
 *   - alt?: string - Alt text for accessibility
 *   - caption?: string - Optional caption text
 */
export function getResourceThumbnailImage(resource: ResourceInstance): ResourceImage | undefined {
  if (resource.images?.thumbnail) {
    return resource.images.thumbnail
  }
  return undefined
}

/**
 * Gets the OpenGraph image for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {ResourceImage | undefined} The OpenGraph image or undefined if not available
 * 
 * @see {@link ResourceImage}
 * @note ResourceImage is an interface with the following properties (as of 2025-03-23):
 *   - url: string - Image URL
 *   - width?: number - Image width in pixels
 *   - height?: number - Image height in pixels
 *   - alt?: string - Alt text for accessibility
 *   - caption?: string - Optional caption text
 */
export function getResourceOgImage(resource: ResourceInstance): ResourceImage | undefined {
  if (resource.images?.og) {
    return resource.images.og
  }
  const metadataOgImage = resource.metadata?.ogImage
  if (metadataOgImage) {
    const dimensionMatch = /_(\d+)x(\d+)(?:_|\.webp$|\.png$|\.jpe?g$)/i.exec(metadataOgImage)
    const width = dimensionMatch?.[1] ? Number(dimensionMatch[1]) : undefined
    const height = dimensionMatch?.[2] ? Number(dimensionMatch[2]) : undefined
    return {
      url: metadataOgImage,
      alt: `${resource.title} glossary preview image`,
      ...(width && height ? { width, height } : {}),
    }
  }
  return undefined
}

/**
 * Gets a fully qualified image URL for a resource image
 * 
 * @param {ResourceImage} image - Resource image object
 * @returns {string} The full image URL
 * 
 * @see {@link ResourceImage}
 * @note ResourceImage is an interface with the following properties (as of 2025-03-23):
 *   - url: string - Image URL
 *   - width?: number - Image width in pixels
 *   - height?: number - Image height in pixels
 *   - alt?: string - Alt text for accessibility
 *   - caption?: string - Optional caption text
 */
export function getResourceImageUrl(image: ResourceImage): string {
  if (!image.url) return ''
  
  return image.url.startsWith('http') 
    ? image.url 
    : `${siteConfig.url}${image.url}`
}

/**
 * Gets the URL for downloading a resource (if applicable)
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} Download URL if applicable, undefined otherwise
 */
export function getResourceDownloadUrl(resource: ResourceInstance): string | undefined {
  if (isTemplate(resource) && 'downloadUrl' in resource && typeof resource.downloadUrl === 'string') {
    return resource.downloadUrl
  }
  
  return undefined
}

/**
 * Gets the steps from a guide (if applicable)
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Array<{title: string, content: string, image?: ResourceImage}>} Array of steps if available, empty array otherwise
 * 
 * @see {@link ResourceImage}
 * @note ResourceImage is an interface with the following properties (as of 2025-03-23):
 *   - url: string - Image URL
 *   - width?: number - Image width in pixels
 *   - height?: number - Image height in pixels
 *   - alt?: string - Alt text for accessibility
 *   - caption?: string - Optional caption text
 */
export function getGuideSteps(resource: ResourceInstance): Array<{title: string; content: string; image?: ResourceImage}> {
  if (isGuide(resource) && 'steps' in resource && Array.isArray(resource.steps)) {
    if (resource.steps.length === 0) return []
    // Transform each step to ensure type safety
    return resource.steps
      .filter((step) => typeof step === 'object' && step !== null)
      .map((step) => {
      const record = step as { title?: string; content?: string; image?: unknown }
      const title = typeof record.title === 'string' ? record.title : ''
      const content = typeof record.content === 'string' ? record.content : ''
      const image = record.image && typeof record.image === 'object' ? record.image as ResourceImage : undefined
      
      return { title, content, image } as const
    })
  }
  
  return []
}

/**
 * Checks if a resource has a specified property
 *
 * @param {ResourceInstance} resource - Any resource instance
 * @param {string} propertyName - The name of the property to check
 * @returns {boolean} True if the resource has the property
 */
export function resourceHasProperty<K extends string>(
  resource: ResourceInstance, 
  propertyName: K
): resource is ResourceInstance & Record<string, unknown> {
  return propertyName in resource
}

/**
 * Gets the difficulty level of a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} Difficulty level if available, undefined otherwise
 */
export function getResourceDifficulty(resource: ResourceInstance): string | undefined {
  if (resourceHasProperty(resource, 'difficulty')) {
    return typeof resource.difficulty === 'string' ? resource.difficulty : undefined
  }
  
  if (isQuiz(resource) && resource.config) {
    const config = resource.config
    if ('difficulty' in config && typeof config.difficulty === 'string') {
      return config.difficulty
    }
  }
  
  return undefined
}

/**
 * Gets the FAQs for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Array<FAQItem>} Array of FAQ items if available, empty array otherwise
 * 
 * @see {@link FAQItem}
 * @note FAQItem is an interface with the following properties (as of 2025-03-23):
 *   - question: string - The FAQ question
 *   - answer: string | ReactNode - The FAQ answer (can be text or React component)
 *   - category: string - Optional category for grouping FAQs
 *   - icon: ReactNode - Optional icon to display next to the question
 */
export function getResourceFaqs(resource: ResourceInstance): Array<FAQItem> {
  if (resourceHasProperty(resource, 'faqs') && Array.isArray(resource.faqs)) {
    // Create a properly typed copy of the faqs to ensure type safety
    return resource.faqs.map(faq => ({
      question: typeof faq.question === 'string' ? faq.question : '',
      answer: faq.answer
    }))
  }
  
  return []
}

/**
 * Gets the specifications from a specs guide
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {unknown} The specifications if available, undefined otherwise
 */
export function getGuideSpecifications(resource: ResourceInstance): unknown {
  if (isSpecsGuide(resource) && resourceHasProperty(resource, 'specifications')) {
    return resource.specifications
  }
  
  return undefined
}

/**
 * Checks if a resource is featured
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {boolean} True if the resource is featured
 */
export function isResourceFeatured(resource: ResourceInstance): boolean {
  return Boolean(resource.featured)
}

/**
 * Checks if a resource matches a given resource type
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @param {ResourceType} type - Resource type to check against
 * @returns {boolean} True if the resource matches the specified type
 * 
 * @see {@link ResourceType}
 * @note ResourceType is a string literal type with the following values (as of 2025-03-23):
 *   "guide" | "tool" | "template" | "case-study" | "ebook" | "glossary-term"
 */
export function matchesResourceType(resource: ResourceInstance, type: ResourceType): boolean {
  return isResourceType(resource, type)
}

/**
 * Gets the author of a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} Author name if available, undefined otherwise
 */
export function getResourceAuthor(resource: ResourceInstance): string | undefined {
  if (resourceHasProperty(resource, 'author')) {
    return typeof resource.author === 'string' ? resource.author : undefined
  }
  
  return undefined
}

/**
 * Gets the date published for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} Date published if available, undefined otherwise
 */
export function getResourcePublishDate(resource: ResourceInstance): string | undefined {
  // Check direct datePublished property
  if (resourceHasProperty(resource, 'datePublished')) {
    return typeof resource.datePublished === 'string' ? resource.datePublished : undefined
  }
  
  // Check metadata.dateCreated
  if (resource.metadata?.dateCreated) {
    return resource.metadata.dateCreated
  }
  
  // Fallback to lastUpdated
  if (resource.lastUpdated) {
    return resource.lastUpdated
  }
  
  return undefined
}

/**
 * Gets the date modified for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} Date modified if available, undefined otherwise
 */
export function getResourceModifiedDate(resource: ResourceInstance): string | undefined {
  const dates: string[] = []
  
  // Collect all possible modification dates
  if (resourceHasProperty(resource, 'dateModified') && typeof resource.dateModified === 'string') {
    dates.push(resource.dateModified)
  }
  
  // Check metadata.dateModified
  if (resource.metadata?.dateModified) {
    dates.push(resource.metadata.dateModified)
  }
  
  // Fallback to lastUpdated
  if (resource.lastUpdated) {
    dates.push(resource.lastUpdated)
  }
  
  // Return the most recent date if any exist
  if (dates.length > 0) {
    return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
  }
  
  return undefined
}

/**
 * Gets the definition text for a glossary term
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string} The definition text if it's a glossary term, empty string otherwise
 */
export function getGlossaryTermDefinition(resource: ResourceInstance): string {
  if (isGlossaryTerm(resource)) {
    return resource.definition ?? resource.shortDescription ?? resource.description ?? ''
  }
  
  return ''
}

/**
 * Gets the single acronym for a resource (only from the explicit acronym field)
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} The acronym if available from the acronym field (no deeper checks), undefined otherwise
 */
export function getResourceAcronym(resource: ResourceInstance): string | undefined {
  if (isGlossaryTerm(resource) && resource.acronym) {
    return resource.acronym
  }
  
  return undefined
}

/**
 * Gets the acronyms for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @param {boolean} [extendedSearch=true] - Whether to search beyond explicit acronym fields (i.e. in metadata keywords, parentheses in title)
 * @returns {string[]} Array of acronyms if available, empty array otherwise
 */
export function getResourceAcronyms(resource: ResourceInstance, extendedSearch = true): string[] {
  // For glossary terms, check the acronym property
  if (isGlossaryTerm(resource) && resource.acronym) {
    return [resource.acronym]
  }
  
  if (!extendedSearch) {
    return []
  }
  
  const acronyms: string[] = []
  
  // Check metadata keywords for potential acronyms
  if (resource.metadata?.keywords) {
    // Filter keywords that look like acronyms (all uppercase, 2+ characters)
    const keywordAcronyms = resource.metadata.keywords
      .filter(keyword => /^[A-Z]{2,}$/.test(keyword))
    
    acronyms.push(...keywordAcronyms)
  }
  
  // Check title for acronyms in parentheses: "Return on Ad Spend (ROAS)"
  const title = getResourceTitle(resource)
  const inParenthesesRegex = /\(([A-Z]{2,})\)/g
  let match
  
  while ((match = inParenthesesRegex.exec(title)) !== null) {
    if (match[1]) acronyms.push(match[1])
  }
  
  // Return unique acronyms
  return [...new Set(acronyms)]
}

/**
 * Gets the metadata for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {MetadataConfig | undefined} The metadata object if available, undefined otherwise
 * 
 * @see {@link MetadataConfig}
 * @note MetadataConfig is an interface with the following properties (as of 2025-03-23):
 *   - title?: string - SEO title override (if different from resource title)
 *   - description?: string - SEO meta description (typically 150-160 characters)
 *   - keywords?: string[] - SEO keywords for search engine indexing
 *   - ogImage?: string - Custom Open Graph image URL override
 *   - canonicalURL?: string - Canonical URL if different from default resource URL
 *   - author?: string - Content author's name
 *   - authorUrl?: string - URL to author's profile or page
 *   - dateCreated?: string - ISO date when resource was first created
 *   - dateModified?: string - ISO date when resource was last modified
 *   - tags?: string[] - Additional tags specifically for SEO purposes
 */
export function getResourceMetadata(resource: ResourceInstance): MetadataConfig | undefined {
  if (resource.metadata && typeof resource.metadata === 'object') {
    return resource.metadata
  }
  return undefined
}

/**
 * Gets the keywords for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @param {boolean} [extendedSearch=true] - Whether to search beyond explicit keyword fields (i.e. in metadata tags)
 * @returns {string[]} Array of keywords if available, empty array otherwise
 * 
 * @important This function returns ALL potential keywords for a resource, including category, acronyms, and tags, NOT just the keywords field.
 * @important If you wish to retrieve only the keywords field, use extendedSearch=false.
 */
export function getResourceKeywords(resource: ResourceInstance, extendedSearch = true): string[] {
  const keywords: string[] = []

  // First check metadata keywords
  if (resource.metadata?.keywords && Array.isArray(resource.metadata.keywords)) {
    keywords.push(...resource.metadata.keywords)
  }
  
  if (!extendedSearch) {
    return keywords.length > 0 ? [...new Set(keywords)] : []
  }
  
  // Then check tags
  if (resource.tags && Array.isArray(resource.tags)) {
    keywords.push(...resource.tags)
  }
  
  // For glossary terms, include category and acronym if available
  if (isGlossaryTerm(resource)) {
    keywords.push(resource.category)
    
    if (resource.acronym) {
      keywords.push(resource.acronym)
    }
  }
  
  // Return unique keywords
  return [...new Set(keywords)]
}

/**
 * Gets the file format of a template
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} The file format if it's a template, undefined otherwise
 */
export function getTemplateFileFormat(resource: ResourceInstance): string | undefined {
  if (isTemplate(resource) && resourceHasProperty(resource, 'fileFormat')) {
    return typeof resource.fileFormat === 'string' ? resource.fileFormat : undefined
  }
  
  return undefined
}

/**
 * Maps template file format to its structured data MIME type equivalent
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string} The MIME type for the template's file format
 */
export function getTemplateStructuredDataFileFormat(resource: ResourceInstance): string {
  if (!isTemplate(resource)) {
    return 'application/octet-stream'
  }
  
  // First check the explicit fileFormat property
  const fileFormat = getTemplateFileFormat(resource)
  if (fileFormat) {
    // Handle explicit file format strings
    if (fileFormat.includes('pdf')) return 'application/pdf'
    if (fileFormat.includes('excel') || fileFormat.includes('xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    if (fileFormat.includes('xls')) return 'application/vnd.ms-excel'
    if (fileFormat.includes('doc')) return 'application/msword'
    if (fileFormat.includes('docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    if (fileFormat.includes('ppt')) return 'application/vnd.ms-powerpoint'
    if (fileFormat.includes('pptx')) return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    if (fileFormat.includes('csv')) return 'text/csv'
    if (fileFormat.includes('json')) return 'application/json'
    if (fileFormat.includes('xml')) return 'application/xml'
    if (fileFormat.includes('html')) return 'text/html'
    if (fileFormat.includes('txt')) return 'text/plain'
  }
  
  // Then check all formats in the config.source.formats array
  if (resource.config?.source?.formats && Array.isArray(resource.config.source.formats)) {
    const formats = resource.config.source.formats
    
    // Check each format in the array
    for (const format of formats) {
      if (!format || typeof format !== 'object') continue
      
      // Use type assertion with string index to avoid type errors
      const formatId = format.formatId as string | undefined
      
      if (formatId) {
        switch (formatId) {
          case 'pdf': return 'application/pdf'
          case 'excel': return 'application/vnd.ms-excel'
          case 'google_sheets': return 'application/vnd.google-apps.spreadsheet'
          case 'google_docs': return 'application/vnd.google-apps.document'
          case 'google_slides': return 'application/vnd.google-apps.presentation'
          case 'word': return 'application/msword'
          case 'powerpoint': return 'application/vnd.ms-powerpoint'
          case 'csv': return 'text/csv'
          case 'json': return 'application/json'
          case 'xml': return 'application/xml'
          case 'html': return 'text/html'
          case 'txt': return 'text/plain'
          case 'canva': return 'application/vnd.canva.document'
          case 'figma': return 'application/vnd.figma.document'
        }
      }
    }
  }
  
  // Default fallback
  return 'application/octet-stream'
}

/**
 * Gets the company name from a case study
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} The company name if it's a case study, undefined otherwise
 */
export function getCaseStudyCompany(resource: ResourceInstance): string | undefined {
  if (isCaseStudy(resource) && resourceHasProperty(resource, 'client')) {
    const client = resource.client
    if (typeof client === 'object' && client !== null && 'name' in client) {
      return typeof client.name === 'string' ? client.name : undefined
    }
  }
  
  return undefined
}

/**
 * Gets the industry from a case study
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} The industry if it's a case study, undefined otherwise
 */
export function getCaseStudyIndustry(resource: ResourceInstance): string | undefined {
  if (isCaseStudy(resource) && resourceHasProperty(resource, 'client')) {
    const client = resource.client
    if (typeof client === 'object' && client !== null && 'industry' in client) {
      return typeof client.industry === 'string' ? client.industry : undefined
    }
  }
  
  return undefined
}

/**
 * Gets the file size of an ebook
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | number | undefined} The file size if it's an ebook, undefined otherwise
 */
export function getEbookFileSize(resource: ResourceInstance): string | number | undefined {
  if (isEbook(resource) && resourceHasProperty(resource, 'fileSize')) {
    const fileSize = resource.fileSize
    return typeof fileSize === 'string' || typeof fileSize === 'number' ? fileSize : undefined
  }

  return undefined
}

/**
 * Gets the PDF byte size of an ebook for structured data.
 *
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {number | undefined} Byte size when the resource is an ebook with `fileSizeBytes`
 */
export function getEbookFileSizeBytes(resource: ResourceInstance): number | undefined {
  if (isEbook(resource) && resourceHasProperty(resource, 'fileSizeBytes')) {
    const fileSizeBytes = resource.fileSizeBytes
    return typeof fileSizeBytes === 'number' ? fileSizeBytes : undefined
  }

  return undefined
}

/**
 * Gets the page count of an ebook
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | number | undefined} The page count if it's an ebook, undefined otherwise
 */
export function getEbookPageCount(resource: ResourceInstance): string | number | undefined {
  if (isEbook(resource) && resourceHasProperty(resource, 'pageCount')) {
    const pageCount = resource.pageCount
    return typeof pageCount === 'string' || typeof pageCount === 'number' ? pageCount : undefined
  }
  
  return undefined
}

/**
 * Gets the guide steps in a format suitable for Schema.org HowTo structured data
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Array<{name: string, text: string, image?: {url: string}}>} Array of steps in Schema.org format
 */
export function getGuideStepsForStructuredData(resource: ResourceInstance): Array<{name: string; text: string; image?: {url: string}}> {
  if (isGuide(resource) && resourceHasProperty(resource, 'steps') && Array.isArray(resource.steps)) {
    // Transform each step to ensure type safety and Schema.org compatibility
    return resource.steps
      .filter((step): step is Record<string, unknown> => 
        typeof step === 'object' && 
        step !== null
      )
      .map(step => {
        const name = typeof step.title === 'string' ? step.title : ''
        const text = typeof step.content === 'string' ? step.content : ''
        let image: {url: string} | undefined = undefined
        
        if (step.image) {
          if (typeof step.image === 'string') {
            image = { url: step.image }
          } else if (typeof step.image === 'object' && step.image !== null && 'url' in step.image) {
            const imageUrl = String(step.image.url)
            if (imageUrl) {
              image = { url: imageUrl }
            }
          }
        }
        
        return { name, text, image }
      })
  }
  
  return []
}

/**
 * Gets the guide type (if available)
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string | undefined} The guide type if it's a guide, undefined otherwise
 */
export function getGuideType(resource: ResourceInstance): string | undefined {
  if (isGuide(resource) && resourceHasProperty(resource, 'guideType')) {
    return typeof resource.guideType === 'string' ? resource.guideType : undefined
  }
  
  return undefined
}

/**
 * Determines if a guide is a how-to guide based on its type or title
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {boolean} True if the resource is a how-to guide
 */
export function isHowToGuide(resource: ResourceInstance): boolean {
  if (!isGuide(resource)) return false
  
  const guideType = getGuideType(resource)
  if (guideType === 'how-to') return true
  
  const title = getResourceTitle(resource)
  const description = getResourceDescription(resource)
  const canonicalTitle =
    'canonicalTitle' in resource && typeof resource.canonicalTitle === 'string'
      ? resource.canonicalTitle
      : ''

  return (
    title.toLowerCase().includes('how to') ||
    description.toLowerCase().includes('how to') ||
    canonicalTitle.toLowerCase().includes('how to')
  )
}

/**
 * Gets tools required for a guide
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string[] | undefined} Array of tools if available, undefined otherwise
 */
export function getGuideTools(resource: ResourceInstance): string[] | undefined {
  if (isGuide(resource) && resourceHasProperty(resource, 'tools') && Array.isArray(resource.tools)) {
    return resource.tools
      .filter((tool): tool is string => typeof tool === 'string')
  }
  
  return undefined
}

/**
 * Gets materials required for a guide
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {string[] | undefined} Array of materials if available, undefined otherwise
 */
export function getGuideMaterials(resource: ResourceInstance): string[] | undefined {
  if (isGuide(resource) && resourceHasProperty(resource, 'materials') && Array.isArray(resource.materials)) {
    return resource.materials
      .filter((material): material is string => typeof material === 'string')
  }
  
  return undefined
}

/**
 * Gets calculator-specific properties for Schema.org structured data
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Record<string, unknown> | undefined} Calculator properties if available, undefined otherwise
 */
export function getCalculatorProperties(resource: ResourceInstance): Record<string, unknown> | undefined {
  if (!isCalculator(resource)) {
    return undefined
  }
  
  const result: Record<string, unknown> = {}
  
  // Check direct properties on the resource
  const propertiesToCheck = [
    'applicationCategory',
    'applicationSubCategory',
    'softwareRequirements',
    'browserRequirements',
    'audience',
    'featureList',
    'headline',
    'interactivityType',
    'educationalUse',
    'educationalAlignment',
    'timeRequired'
  ]
  
  // Check each property on the resource itself
  for (const prop of propertiesToCheck) {
    if (resourceHasProperty(resource, prop)) {
      result[prop] = resource[prop]
    }
  }
  
  // Check properties in the config object if it exists
  if (resourceHasProperty(resource, 'config') && resource.config && typeof resource.config === 'object') {
    const config = resource.config
    
    for (const prop of propertiesToCheck) {
      if (prop in config && result[prop] === undefined) {
        result[prop] = config[prop as keyof typeof config]
      }
    }
    
    // Check nested properties in config.calculator if it exists
    if ('calculator' in config && config.calculator && typeof config.calculator === 'object') {
      const calculatorConfig = config.calculator
      
      for (const prop of propertiesToCheck) {
        if (prop in calculatorConfig && result[prop] === undefined) {
          result[prop] = calculatorConfig[prop as keyof typeof calculatorConfig]
        }
      }
    }
  }
  
  return Object.keys(result).length > 0 ? result : undefined
}

/**
 * Gets generator-specific properties for Schema.org structured data
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Record<string, unknown> | undefined} Generator properties if available, undefined otherwise
 */
export function getGeneratorProperties(resource: ResourceInstance): Record<string, unknown> | undefined {
  if (!isGenerator(resource)) {
    return undefined
  }
  
  const result: Record<string, unknown> = {}
  
  // Check direct properties on the resource
  const propertiesToCheck = [
    'applicationCategory',
    'applicationSubCategory',
    'softwareRequirements',
    'browserRequirements',
    'audience',
    'featureList',
    'headline',
    'interactivityType',
    'educationalUse',
    'educationalAlignment',
    'timeRequired',
    'assesses',
    'teaches',
    'format',
    'mimeType',
  ]
  
  // Check each property on the resource itself
  for (const prop of propertiesToCheck) {
    if (resourceHasProperty(resource, prop)) {
      result[prop] = resource[prop]
    }
  }
  
  // Check properties in the config object if it exists
  if (resourceHasProperty(resource, 'config') && resource.config && typeof resource.config === 'object') {
    const config = resource.config
    
    // Check base config properties
    for (const prop of propertiesToCheck) {
      if (prop in config && result[prop] === undefined) {
        result[prop] = config[prop as keyof typeof config]
      }
    }

    // Check output configuration
    if ('output' in config && config.output && typeof config.output === 'object') {
      const output = config.output
      if (!result.outputFormat && 'format' in output) {
        result.outputFormat = output.format
      }
      if (!result.outputMimeType && 'mimeType' in output) {
        result.outputMimeType = output.mimeType
      }
    }

    // Check generator-specific config
    if ('generator' in config && config.generator && typeof config.generator === 'object') {
      const generatorConfig = config.generator
      
      for (const prop of propertiesToCheck) {
        if (prop in generatorConfig && result[prop] === undefined) {
          result[prop] = generatorConfig[prop as keyof typeof generatorConfig]
        }
      }
    }
  }
  
  return Object.keys(result).length > 0 ? result : undefined
}

/**
 * Gets quiz-specific properties for Schema.org structured data
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Record<string, unknown> | undefined} Quiz properties if available, undefined otherwise
 */
export function getQuizProperties(resource: ResourceInstance): Record<string, unknown> | undefined {
  if (isQuiz(resource) && resourceHasProperty(resource, 'config')) {
    const config = resource.config
    if (!config || typeof config !== 'object') return undefined
    
    const result: Record<string, unknown> = {}
    
    // Extract quiz-specific properties
    if ('educationalLevel' in config && typeof config.educationalLevel === 'string') {
      result.educationalLevel = config.educationalLevel
    }
    
    if ('timeRequired' in config && typeof config.timeRequired === 'string') {
      result.timeRequired = config.timeRequired
    } else if ('estimatedTime' in resource && typeof resource.estimatedTime === 'string') {
      result.timeRequired = resource.estimatedTime
    }
    
    if ('educationalUse' in config && typeof config.educationalUse === 'string') {
      result.educationalUse = config.educationalUse
    }
    
    if ('learningResourceType' in config && typeof config.learningResourceType === 'string') {
      result.learningResourceType = config.learningResourceType
    }
    
    if ('interactivityType' in config && typeof config.interactivityType === 'string') {
      result.interactivityType = config.interactivityType
    }
    
    // Add additional quiz properties
    if ('assesses' in config && typeof config.assesses === 'string') {
      result.assesses = config.assesses
    }
    
    if ('teaches' in config && typeof config.teaches === 'string') {
      result.teaches = config.teaches
    }

    if ('audience' in config && typeof config.audience === 'string') {
      result.audience = config.audience
    }
    
    return Object.keys(result).length > 0 ? result : undefined
  }
  
  return undefined
}

/**
 * Converts internal difficulty level to Schema.org format
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {'Beginner' | 'Intermediate' | 'Advanced' | 'Expert' | undefined} Schema.org compatible difficulty level
 */
export function getSchemaOrgDifficultyLevel(resource: ResourceInstance): 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert' | undefined {
  const difficulty = getResourceDifficulty(resource)
  
  if (!difficulty) return undefined
  
  // Map our lowercase values to Schema.org's capitalized values
  const difficultyMap: Record<string, 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert'> = {
    'beginner': 'Beginner',
    'intermediate': 'Intermediate',
    'advanced': 'Advanced',
    'expert': 'Expert'
  }
  
  return difficultyMap[difficulty.toLowerCase()] ?? 'Intermediate'
}

/**
 * Gets all available formats for a template with their full information
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Array<{formatId: string, contentUrl: string, fileFormat: string, metadata?: {version?: string, lastUpdated?: string, fileSize?: string, pageCount?: number}}}>
 *   Array of format objects with download URLs and format information
 */
export function getTemplateFormats(resource: ResourceInstance): Array<{
  formatId: string 
  contentUrl: string 
  fileFormat: string 
  metadata?: {
    version?: string 
    lastUpdated?: string 
    fileSize?: string 
    pageCount?: number
  }
}> {
  if (!isTemplate(resource) || !resourceHasProperty(resource, 'config')) {
    return []
  }
  
  const config = resource.config
  if (!config || typeof config !== 'object' || !('source' in config)) {
    return []
  }
  
  const source = config.source
  if (!source || typeof source !== 'object' || !('formats' in source) || !Array.isArray(source.formats)) {
    return []
  }
  
  // Extract valid formats with type safety
  return source.formats
    .filter(format => {
      // Basic type check
      if (typeof format !== 'object' || format === null) return false
      
      // Check required properties
      return 'formatId' in format && 
             'contentUrl' in format && 
             typeof format.formatId === 'string' && 
             typeof format.contentUrl === 'string'
    })
    .map(format => {
      // We've already verified these properties exist in the filter
      const formatId = format.formatId as string
      const contentUrl = format.contentUrl
      const fileFormat = getTemplateFileFormatById(formatId)
      
      // Extract metadata if available
      let metadata: {version?: string; lastUpdated?: string; fileSize?: string; pageCount?: number} | undefined = undefined
      
      if ('metadata' in format && format.metadata && typeof format.metadata === 'object') {
        const meta = format.metadata as Record<string, unknown>
        metadata = {
          version: typeof meta.version === 'string' ? meta.version : undefined,
          lastUpdated: typeof meta.lastUpdated === 'string' ? meta.lastUpdated : undefined,
          fileSize: typeof meta.fileSize === 'string' ? meta.fileSize : undefined,
          pageCount: typeof meta.pageCount === 'number' ? meta.pageCount : undefined
        }
      }
      
      return {
        formatId,
        contentUrl,
        fileFormat,
        metadata
      }
    })
}

/**
 * Gets the MIME type for a template format ID
 * 
 * @param {string} formatId - Format identifier
 * @returns {string} The MIME type for the format
 */
export function getTemplateFileFormatById(formatId: string): string {
  switch (formatId) {
    case 'pdf': return 'application/pdf'
    case 'excel': return 'application/vnd.ms-excel'
    case 'google_sheets': return 'application/vnd.google-apps.spreadsheet'
    case 'google_docs': return 'application/vnd.google-apps.document'
    case 'google_slides': return 'application/vnd.google-apps.presentation'
    case 'word': return 'application/msword'
    case 'powerpoint': return 'application/vnd.ms-powerpoint'
    case 'csv': return 'text/csv'
    case 'json': return 'application/json'
    case 'xml': return 'application/xml'
    case 'html': return 'text/html'
    case 'txt': return 'text/plain'
    case 'canva': return 'application/vnd.canva.document'
    case 'figma': return 'application/vnd.figma.document'
    default: return 'application/octet-stream'
  }
}

/**
 * Gets references to related resources
 * 
 * This function collects all explicit relationships defined in a resource:
 * - For glossary terms: Related terms referenced by slug
 * - For supplemental resources: Internal resources referenced by resourceSlug
 * - For explicit related resources: Resources referenced by slug or direct string reference
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {Array<{slug: string, resourceType?: string}>} Array of related resource references
 * 
 * @example
 * const relatedRefs = getResourceRelatedRefs(glossaryTerm)
 * // Returns: [{ slug: 'roi-calculator', resourceType: 'tool' }, { slug: 'cpm', resourceType: 'glossary-term' }]
 */
export function getResourceRelatedRefs(resource: ResourceInstance): Array<{slug: string; resourceType?: string}> {
  const relatedRefs: Array<{slug: string; resourceType?: string}> = []
  
  // Check for glossary term relationships
  if (isGlossaryTerm(resource) && Array.isArray(resource.relatedTerms)) {
    const termRefs = resource.relatedTerms
      .filter((term): term is RelatedTerm => 
        typeof term === 'object' && 
        term !== null && 
        typeof term.slug === 'string' &&
        typeof term.title === 'string'
      )
      .map(term => ({
        slug: term.slug,
        resourceType: 'glossary-term'
      }))
    relatedRefs.push(...termRefs)
  }
  
  // Check for supplemental resources
  if ('supplementalResources' in resource && 
      Array.isArray(resource.supplementalResources)) {
    const supplementalRefs = resource.supplementalResources.reduce<Array<{slug: string; resourceType?: string}>>((acc, r) => {
      if (r.isInternal && 
          typeof r.resourceSlug === 'string' && 
          typeof r.resourceType === 'string') {
        acc.push({
          slug: r.resourceSlug,    // TypeScript flow analysis works here
          resourceType: r.resourceType
        })
      }
      return acc
    }, [])
    relatedRefs.push(...supplementalRefs)
  }
  
  // Check for explicit related resources array
  if ('relatedResources' in resource && 
      Array.isArray(resource.relatedResources)) {
    const resourceRefs = resource.relatedResources
      .map(r => {
        if (typeof r === 'string') {
          return { slug: r }
        }
        if (typeof r === 'object' && r !== null && typeof r.slug === 'string') {
          return {
            slug: r.slug,
            resourceType: 'resourceType' in r ? r.resourceType as string : undefined
          }
        }
        return null
      })
      .filter((ref): ref is {slug: string; resourceType?: string} => 
        ref !== null && typeof ref.slug === 'string'
      )
    relatedRefs.push(...resourceRefs)
  }
  
  // Remove duplicates by slug
  return Array.from(
    new Map(relatedRefs.map(ref => [ref.slug, ref])).values()
  )
}

/**
 * Safely extracts calculation properties from a MetricTerm.
 * Returns default values for missing properties to ensure type safety.
 * 
 * @param {unknown} term - The term to extract calculation properties from
 * @returns {Object} An object containing all calculation properties with safe defaults
 * 
 * @example
 * ```ts
 * const metricTerm = getGlossaryTermById('bounce-rate')
 * const calculation = getMetricCalculationProperties(metricTerm)
 * // Now safely use calculation.formula, calculation.components, etc.
 * ```
 */
export function getMetricCalculationProperties(term: unknown): {
  components: Array<{name: string; description: string}>
  formula: string
  operation: OperationType | undefined
  resultUnit: '%' | '$' | 'x' | 'ratio' | '' | undefined
  explanation: string
  variables: Array<{name: string; description: string}>
} {
  // Define the return type with explicit structure
  type CalculationProperties = {
    components: Array<{name: string; description: string}>
    formula: string
    operation: OperationType | undefined
    resultUnit: '%' | '$' | 'x' | 'ratio' | '' | undefined
    explanation: string
    variables: Array<{name: string; description: string}>
  }

  // Default return object with safe values
  const defaultResult: CalculationProperties = {
    components: [],
    formula: '',
    operation: undefined,
    resultUnit: undefined,
    explanation: '',
    variables: []
  }

  // Type guard to check if term is a valid MetricTerm
  function isMetricTermWithCalculation(value: unknown): value is {
    category: string
    calculation: {
      components?: Array<{name: string; description: string}>
      formula?: string
      operation?: OperationType
      resultUnit?: '%' | '$' | 'x' | 'ratio' | ''
      explanation?: string
      variables?: Array<{name: string; description: string}>
    }
  } {
    return (
      typeof value === 'object' && 
      value !== null &&
      'category' in value &&
      value.category === 'metrics' &&
      'calculation' in value &&
      typeof value.calculation === 'object' &&
      value.calculation !== null
    )
  }

  // Use type guard for safe property access
  if (!isMetricTermWithCalculation(term)) {
    return defaultResult
  }

  const { calculation } = term

  // Use nullish coalescing and optional chaining for safe property access
  return {
    components: Array.isArray(calculation.components) ? calculation.components : [],
    formula: typeof calculation.formula === 'string' ? calculation.formula : '',
    operation: calculation.operation,
    resultUnit: calculation.resultUnit,
    explanation: typeof calculation.explanation === 'string' ? calculation.explanation : '',
    variables: Array.isArray(calculation.variables) ? calculation.variables : []
  }
}

/**
 * Safely extracts variable names from a MetricTerm's calculation properties.
 * Returns an empty array if no variables are found.
 * 
 * @param {unknown} term - The term to extract variable names from
 * @returns {string[]} Array of variable names
 * 
 * @example
 * ```ts
 * const metricTerm = getGlossaryTermById('conversion-rate')
 * const variableNames = getMetricTermVariableNames(metricTerm)
 * // Returns ['visitors', 'conversions'] if those are the variables
 * ```
 */
export function getMetricTermVariableNames(term: unknown): string[] {
  // Type guard to check if term is a valid MetricTerm with calculation variables
  function isMetricTermWithVariables(value: unknown): value is {
    category: string
    calculation: {
      variables?: Array<{name: string; description: string}>
    }
  } {
    return (
      typeof value === 'object' && 
      value !== null &&
      'category' in value &&
      value.category === 'metrics' &&
      'calculation' in value &&
      typeof value.calculation === 'object' &&
      value.calculation !== null
    )
  }

  // Use type guard for safe property access
  if (!isMetricTermWithVariables(term)) {
    return []
  }

  const { calculation } = term

  // Extract variable names if they exist
  if (Array.isArray(calculation.variables)) {
    return calculation.variables
      .filter(variable => typeof variable.name === 'string')
      .map(variable => variable.name)
  }

  return []
}

/**
 * Gets the template type for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {TemplateType | undefined} The template type if it's a template, undefined otherwise
 * 
 * @see {@link TemplateType}
 * @note TemplateType is a string literal type with values like 'buyer_persona', 'campaign_brief', etc.
 */
export function getTemplateType(resource: ResourceInstance): TemplateType | undefined {
  if (isTemplate(resource)) {
    return resource.templateType
  }
  
  return undefined
}

/**
 * Gets the template category for a resource
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {TemplateCategoryType | undefined} The template category if it's a template, undefined otherwise
 * 
 * @see {@link TemplateCategoryType}
 * @note TemplateCategoryType is a string literal type with values like 'campaign_planning', 'content_strategy', etc.
 */
export function getTemplateCategory(resource: ResourceInstance): TemplateCategoryType | undefined {
  if (isTemplate(resource)) {
    return resource.category
  }
  
  return undefined
}

/**
 * Checks if a template has gated formats
 * 
 * @param {ResourceInstance} resource - Any resource instance
 * @returns {boolean} True if the template has gated formats, false otherwise
 */
export function templateHasGatedFormats(resource: ResourceInstance): boolean {
  if (isTemplate(resource) && resource.config?.access) {
    return resource.config.access.hasGatedFormats
  }
  
  return false
}
